import './MicrolinkPreview.scss';
import { prefixClass } from 'lib/utils';
import { FC, useEffect, useState } from 'react';

import type {
  ArcadePreviewExtra,
  LinkPreviewExtra,
} from 'presentation/components/KSQ2/Cards/MessagesCard/subcomponents/MessageContent/analyzeMessageContent';
import classNames from 'classnames';
import { IconKS } from 'components/Icons';

interface MicrolinkImage {
  url?: string;
}

interface MicrolinkData {
  title?: string;
  description?: string;
  url?: string;
  image?: MicrolinkImage;
  logo?: MicrolinkImage;
}

interface MicrolinkApiResponse {
  status?: string;
  data?: MicrolinkData;
}

const microlinkCache = new Map<string, MicrolinkData>();

export interface MicrolinkPreviewProps {
  extra: ArcadePreviewExtra | LinkPreviewExtra;
}

const MicrolinkPreview: FC<MicrolinkPreviewProps> = ({ extra }) => {
  const prefix = prefixClass('q2-message-preview-module-microlink');

  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [data, setData] = useState<MicrolinkData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const type = extra.type;

  const KSDetected = extra.url.includes('.kitchensync.us');

  useEffect(() => {
    const url = extra.url;
    if (!url) {
      setStatus('idle');
      setData(null);
      setError(null);
      return;
    }

    if (microlinkCache.has(url)) {
      setData(microlinkCache.get(url) ?? null);
      setStatus('success');
      setError(null);
      return;
    }

    if (typeof fetch === 'undefined') {
      setStatus('error');
      setError('Preview unavailable');
      return;
    }

    let cancelled = false;
    setStatus('loading');
    setError(null);

    fetch(`https://api.microlink.io/?url=${encodeURIComponent(url)}`)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Microlink error: ${response.status}`);
        }
        return response.json();
      })
      .then((payload: MicrolinkApiResponse) => {
        if (cancelled) {
          return;
        }

        if (payload?.status === 'success' && payload.data) {
          microlinkCache.set(url, payload.data);
          setData(payload.data);
          setStatus('success');
        } else {
          throw new Error('Microlink response missing data');
        }
      })
      .catch(fetchError => {
        if (cancelled) {
          return;
        }
        // eslint-disable-next-line no-console
        console.error('Microlink fetch failed', fetchError);
        setStatus('error');
        setError('Preview unavailable');
      });

    return () => {
      cancelled = true;
    };
  }, [extra]);

  const targetUrl = data?.url || extra.url;

  return (
    <div
      className={classNames(prefix(), {
        'arcade-preview': type === 'arcade',
        'link-preview': type === 'link',
        'ks-preview': KSDetected,
      })}
    >
      {status === 'loading' && <div className={prefix('loading')}>Loading preview…</div>}
      {status === 'error' && error && <div className={prefix('error')}>{error}</div>}
      {status === 'success' && data && targetUrl && (
        <a href={targetUrl} target="_blank" rel="noopener noreferrer" className={prefix('card')}>
          {data.image?.url && !KSDetected && (
            <div className={prefix('card-image')}>
              <img src={data.image.url} alt={data.title ?? 'Link preview'} />
            </div>
          )}
          {KSDetected && (
            <div className={prefix('card-image')}>
              <IconKS />
            </div>
          )}
          <div className={prefix('card-body')}>
            {data.title && <div className={prefix('card-title')}>{data.title}</div>}
            {data.description && <div className={prefix('card-description')}>{data.description}</div>}
            {type === 'link' && <div className={prefix('card-url')}>{targetUrl}</div>}
          </div>
        </a>
      )}
    </div>
  );
};

export default MicrolinkPreview;
